import React, { createContext, useContext, useCallback, useMemo, useRef, useState } from "react";
import type { Variant } from "@/lib/landing/cinematicTransitions";

export interface StartTransitionOptions {
  to: string;
  variant: Variant;
  originPoint?: { x: number; y: number };
  preload?: () => Promise<unknown>;
}

export interface CinematicContextValue {
  startTransition: (opts: StartTransitionOptions) => void;
  isTransitioning: boolean;
  currentOptions: StartTransitionOptions | null;
  /**
   * Monotonic id, bumped once per started transition. The layer keys
   * its single driving effect on this and guards navigate()/finish by
   * it, so the timeline starts and navigates EXACTLY ONCE per
   * transition even if the effect re-runs (root-cause fix for the
   * double-history-push / "Back needs two presses" bug).
   */
  transitionId: number;
  onNavigated: () => void;
  onAnimationComplete: () => void;
}

export const CinematicContext = createContext<CinematicContextValue | undefined>(undefined);

export function useCinematicContext(): CinematicContextValue | undefined {
  return useContext(CinematicContext);
}

interface Props {
  children: React.ReactNode;
}

export function CinematicTransitionProvider({ children }: Props) {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [currentOptions, setCurrentOptions] = useState<StartTransitionOptions | null>(null);
  // Bumped once per started transition. The layer's single driver
  // effect keys on this; navigate() is guarded by it → exactly one
  // navigate per transition (fixes the double-history-push).
  const [transitionId, setTransitionId] = useState(0);
  const navigatedRef = useRef<(() => void) | null>(null);
  const completeRef = useRef<(() => void) | null>(null);

  const startTransition = useCallback((opts: StartTransitionOptions) => {
    setTransitionId((n) => n + 1);
    setIsTransitioning(true);
    setCurrentOptions(opts);
  }, []);

  const onNavigated = useCallback(() => {
    navigatedRef.current?.();
  }, []);

  const onAnimationComplete = useCallback(() => {
    setCurrentOptions(null);
    setIsTransitioning(false);
    completeRef.current?.();
  }, []);

  // Memoized so the context object is referentially stable across
  // provider re-renders. The layer's single driver effect can then key
  // strictly on `transitionId` (not on a new `ctx` object every
  // render), which — together with the per-transition navigate guard —
  // is the root-cause fix for the double-history-push bug.
  const value = useMemo<CinematicContextValue>(
    () => ({
      startTransition,
      isTransitioning,
      currentOptions,
      transitionId,
      onNavigated,
      onAnimationComplete,
    }),
    [
      startTransition,
      isTransitioning,
      currentOptions,
      transitionId,
      onNavigated,
      onAnimationComplete,
    ],
  );

  return (
    <CinematicContext.Provider value={value}>
      {children}
    </CinematicContext.Provider>
  );
}
